/**
 * Configuration Service
 * Fetches runtime configuration from public/config.json
 * This allows updating maintenance mode without rebuilding the app
 */

class ConfigService {
    constructor() {
        this.config = null;
        this.lastFetch = 0;
        this.cacheTimeout = 30000; // 30 seconds cache
    }

    /**
     * Fetch configuration from public/config.json
     * @returns {Promise<Object>} Configuration object
     */
    async fetchConfig() {
        const now = Date.now();
        
        // Return cached config if still valid
        if (this.config && (now - this.lastFetch) < this.cacheTimeout) {
            return this.config;
        }

        try {
            // Add cache buster to ensure fresh config
            const response = await fetch(`/config.json?t=${now}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch config: ${response.status}`);
            }
            
            this.config = await response.json();
            this.lastFetch = now;
            
            return this.config;
        } catch (error) {
            console.error('[ConfigService] Failed to load config:', error);
            
            // Return default config if fetch fails
            const defaultConfig = {
                maintenanceBanner: 0,
                maintenanceMessage: "Website Under Maintenance - You may experience some service disruptions. We're working to resolve any issues as quickly as possible."
            };
            
            this.config = defaultConfig;
            return defaultConfig;
        }
    }

    /**
     * Check if maintenance banner should be shown
     * @returns {Promise<boolean>}
     */
    async isMaintenanceBannerEnabled() {
        const config = await this.fetchConfig();
        return config.maintenanceBanner === 1;
    }

    /**
     * Get maintenance message
     * @returns {Promise<string>}
     */
    async getMaintenanceMessage() {
        const config = await this.fetchConfig();
        return config.maintenanceMessage;
    }

    /**
     * Determine if NO_SERVER (serverless/mock) mode is enabled.
     * Priority: Demo build type -> localStorage.NO_SERVER -> runtime config (NO_SERVER|noServer) -> env (REACT_APP_NO_SERVER)
     */
    async isNoServerEnabled() {
        // Demo builds always use NO_SERVER mode (static, not configurable)
        const { isDemoBuild } = await import('../config/AppStatic.js');
        if (isDemoBuild()) {
            return true;
        }

        try {
            const ls = localStorage.getItem('NO_SERVER');
            if (ls === 'true' || ls === '1') return true;
            if (ls === 'false' || ls === '0') return false;
        } catch (e) {}

        const config = await this.fetchConfig();
        if (config && (config.NO_SERVER === true || config.NO_SERVER === 1 || config.noServer === true || config.noServer === 1)) {
            return true;
        }

        // Build-time env fallback
        const envFlag = process.env.REACT_APP_NO_SERVER || process.env.NO_SERVER;
        if (typeof envFlag !== 'undefined') {
            const val = String(envFlag).toLowerCase();
            return val === 'true' || val === '1';
        }

        // Optional window flag for quick toggling in console
        if (typeof window !== 'undefined' && window.NO_SERVER === true) return true;

        return false;
    }

    /**
     * Clear cache to force reload on next request
     */
    clearCache() {
        this.config = null;
        this.lastFetch = 0;
    }
}

// Export singleton instance
export const configService = new ConfigService();
export default configService; 